import {
  Component,
  OnInit,
  inject,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  effect,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormGroupDirective,
  Validators,
  FormControl,
  NgForm,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatDialogModule, MatDialog } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { Store } from "@ngrx/store";
import { Subject, takeUntil, debounceTime, take, map, distinctUntilChanged } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  selectActivePacket,
  selectTraditionalIA,
  selectMvpIA,
  selectRelatedOrganizations,
  selectEffectiveQuestionnaire,
  selectIsSaving,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { selectUserAdminFilteredOrgs } from "@store/selectors/user-admin/user-admin.selector";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { ImprovementActivity } from "@models/mips-stepper.model";
import {
  IA_ACTIVITIES_2025,
  IA_ACTIVITIES_2026,
  IA_CATEGORY_ORDER_2025,
  IA_CATEGORY_ORDER_2026,
} from "./ia-measures-data";
import { getMipsPerformanceDates } from "../../constants/mips-constants";
import { MAX_FILE_SIZE } from '@constants/file-upload';
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { ErrorStateMatcher } from "@angular/material/core";
import { MipsReweightingService } from "@services/mips-reweighting.service";
import { MipsValidationService } from "@services/mips-validation.service";

interface IACategory {
  name: string;
  activities: ImprovementActivity[];
  isExpanded: boolean;
}

export class CustomErrorStateMatcher implements ErrorStateMatcher {
  constructor(private isInvalid: () => boolean) {}

  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    return this.isInvalid();
  }
}

@Component({
  selector: "app-mips-ia",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatDialogModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatSnackBarModule,
  ],
  templateUrl: "./mips-ia.component.html",
  styleUrls: ["./mips-ia.component.css"],
})
export class MipsImprovementActivitiesComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly ctrlContainer = inject(FormGroupDirective);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  public readonly cdr = inject(ChangeDetectorRef);
  private readonly reweightingService = inject(MipsReweightingService);
  private readonly validationService = inject(MipsValidationService);

  questionnaire = toSignal(this.store.select(selectEffectiveQuestionnaire));
  reweighting = this.reweightingService.getReweightingSignal();
  private readonly destroy$ = new Subject<void>();
  private readonly activityChanged$ = new Subject<void>();

  readonly minDate = getMipsPerformanceDates().MIN_DATE;
  readonly maxDate = getMipsPerformanceDates().MAX_DATE;

  performanceStartDateInvalid = false;
  performanceEndDateInvalid = false;
  startDateErrorMatcher!: ErrorStateMatcher;
  endDateErrorMatcher!: ErrorStateMatcher;
  dateRangeInvalid = false;
  startDateBeforeEndDateError = false;

  currentPacket = toSignal(this.store.select(selectActivePacket));
  isScoreCalculating = toSignal(this.store.select(selectIsSaving), { initialValue: false });
  relatedOrganizations = toSignal(this.store.select(selectRelatedOrganizations), { initialValue: [] });
  multiPacketMode = toSignal(
    this.store.select((state: any) => state.mipsPacket?.multiPacketMode),
  );
  traditionalIA = toSignal(this.store.select(selectTraditionalIA));
  mvpIA = toSignal(this.store.select(selectMvpIA));

  iaForm!: FormGroup;
  allActivities: ImprovementActivity[] = [];
  traditionalActivities: ImprovementActivity[] = [];
  mvpActivities: ImprovementActivity[] = [];
  traditionalSelectedIds: Set<string> = new Set();
  mvpSelectedIds: Set<string> = new Set();
  categories: IACategory[] = [];
  performanceStartDate: Date | null = null;
  performanceEndDate: Date | null = null;
  currentTab: "traditional" | "mvp" = "traditional";

  private lastLoadedActivitiesHash: string = "";
  private lastLoadedPerformanceYear: number | undefined;

  constructor() {
    effect(() => {
      const packet = this.currentPacket();
      const mode = this.multiPacketMode();
      const traditionalIA = this.traditionalIA();
      const mvpIA = this.mvpIA();

      const performanceYear = packet?.performanceYear;
      if (performanceYear !== undefined && performanceYear !== this.lastLoadedPerformanceYear) {
        this.lastLoadedPerformanceYear = performanceYear;
        this.loadActivities();
        this.lastLoadedActivitiesHash = "";
      }

      const hasData = mode?.enabled
        ? (!!traditionalIA || !!mvpIA)
        : !!packet?.improvementActivities;

      if (hasData) {
        let backendActivitiesHash: string;
        if (mode?.enabled) {
          backendActivitiesHash = JSON.stringify({
            traditional: traditionalIA?.activities || [],
            mvp: mvpIA?.activities || [],
          });
        } else {
          backendActivitiesHash = JSON.stringify({
            packetId: packet?.id,
            activities: packet?.improvementActivities?.activities || [],
          });
        }

        if (backendActivitiesHash !== this.lastLoadedActivitiesHash) {
          this.lastLoadedActivitiesHash = backendActivitiesHash;

          if (mode?.enabled) {
            this.loadFromPacket(traditionalIA, mvpIA);
          } else {
            this.loadFromPacket(packet?.improvementActivities);
          }

          this.loadDatesFromPacket();
          this.organizeCategories();
          this.cdr.markForCheck();

        } 

        this.updateParentFormValidity();
      } else {
        const hadData = this.lastLoadedActivitiesHash !== "";
        if (hadData) {
          this.lastLoadedActivitiesHash = "";
          this.resetActivities();
          this.loadDatesFromPacket();
          this.updateParentFormValidity();
        }
        this.organizeCategories();
        this.cdr.markForCheck();
      }
    });

  }

  // ============================================
  // COMPUTED PROPERTIES
  // ============================================

  get practice(): string {
    const practiceId = this.currentPacket()?.practice || "";
    if (!practiceId) return "";

    let orgName = practiceId;

    this.store
      .select(selectUserAdminFilteredOrgs)
      .pipe(take(1))
      .subscribe((orgs) => {
        if (orgs) {
          const org = orgs.find((o) => o.id.toString() === practiceId);
          if (org) {
            orgName = org.name;
          }
        }
      });

    return orgName;
  }

  get rollupTooltip(): string {
    const orgs = [this.practice, ...this.relatedOrganizations().map(o => o.organizationName)];
    return orgs.filter(Boolean).join(", ");
  }

  get performanceDuration(): string {
    const packet = this.currentPacket();
    const start = packet?.tinDetails?.performanceStartDate;
    const end = packet?.tinDetails?.performanceEndDate;
    if (start && end) {
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    }
    return "";
  }

  get tin(): string {
    return this.currentPacket()?.tinDetails?.tin ?? "";
  }

  get reportingType(): string {
    const mode = this.multiPacketMode();
    if (mode?.enabled && mode?.mode) {
      return mode.mode;
    }
    return this.currentPacket()?.reportingType || "traditional";
  }

  get showTabs(): boolean {
    return this.reportingType === "both";
  }

  get activeActivities(): ImprovementActivity[] {
    if (!this.showTabs) {
      return this.reportingType === "mvp"
        ? this.mvpActivities
        : this.traditionalActivities;
    }
    return this.currentTab === "mvp"
      ? this.mvpActivities
      : this.traditionalActivities;
  }

  get hasSettingsSpecialStatus(): boolean {
    const questionnaire = this.questionnaire();
    return (
      questionnaire?.specialStatusSmall === "yes" ||
      questionnaire?.specialStatusNonPatientFacing === "yes" ||
      questionnaire?.specialStatusRural === "yes" ||
      questionnaire?.specialStatusHpsa === "yes"
    );
  }

  get hasSpecialStatus(): boolean {
    const reportingType = this.reportingType;

    if (reportingType === "mvp") {
      return true;
    }

    if (reportingType === "both") {
      return this.currentTab === "mvp" ? true : this.hasSettingsSpecialStatus;
    }

    return this.hasSettingsSpecialStatus;
  }


  get selectedActivities(): ImprovementActivity[] {
    const selectedSet =
      (this.showTabs && this.currentTab === "mvp") ||
      (!this.showTabs && this.reportingType === "mvp")
        ? this.mvpSelectedIds
        : this.traditionalSelectedIds;

    return this.activeActivities.filter((a) => selectedSet.has(a.activityId));
  }

  get traditionalSelectedCount(): number {
    return this.traditionalActivities.filter((a) =>
      this.traditionalSelectedIds.has(a.activityId),
    ).length;
  }

  get mvpSelectedCount(): number {
    return this.mvpActivities.filter((a) =>
      this.mvpSelectedIds.has(a.activityId),
    ).length;
  }

  get activitiesSelected(): number {
    return this.selectedActivities.length;
  }

  get minimumRequired(): number {
    return this.hasSpecialStatus ? 1 : 2;
  }

  // Computed live from currently selected activities rather than read from the packet/store —
  // those only reflect the last completed save (up to 2s behind selection changes via
  // activityChanged$'s debounce, and never at all if the selection was emptied out).
  get estimatedTotal(): number {
    const reportingType = this.currentPacket()?.reportingType || "traditional";
    return reportingType === "mvp"
      ? this.sumSelectedActivityPoints(this.mvpActivities, this.mvpSelectedIds)
      : this.sumSelectedActivityPoints(this.traditionalActivities, this.traditionalSelectedIds);
  }

  get estimatedWeight(): number {
    return this.weightFromPoints(this.estimatedTotal);
  }

  get estimatedWeightTraditional(): number {
    return this.weightFromPoints(this.estimatedTotalTraditional);
  }

  get estimatedWeightMvp(): number {
    return this.weightFromPoints(this.estimatedTotalMvp);
  }

  private sumSelectedActivityPoints(activities: ImprovementActivity[], selectedIds: Set<string>): number {
    const total = activities
      .filter((a) => selectedIds.has(a.activityId))
      .reduce((sum, a) => sum + this.getActivityPoints(a), 0);
    return Math.min(total, 40);
  }

  private weightFromPoints(points: number): number {
    const maxWeight = this.reweighting().weights.ia;
    return Math.min(Math.round((points / 40) * maxWeight), maxWeight);
  }

  get reweightingTooltip(): string {
    return this.reweighting().explanationTooltip;
  }

  get showReweightingIndicator(): boolean {
    return this.reweighting().isReweighted;
  }

  get requirementMet(): boolean {
    return this.estimatedTotal >= 40;
  }

  get canProceedToNextStep(): boolean {
    if (this.isInBothMode) {
      return (
        this.traditionalRequirementMet &&
        this.mvpRequirementMet &&
        this.performanceStartDate !== null &&
        this.performanceEndDate !== null
      );
    }

    return (
      this.requirementMet &&
      this.performanceStartDate !== null &&
      this.performanceEndDate !== null
    );
  }

  get specialStatusText(): string {
    const questionnaire = this.questionnaire();
    const reportingType = this.reportingType;

    if (reportingType === "mvp") {
      return "MVP Participant";
    }

    if (reportingType === "both" && this.currentTab === "mvp") {
      return "MVP Participant";
    }

    const reasons: string[] = [];
    if (questionnaire?.specialStatusSmall === "yes") reasons.push("Small Practice");
    if (questionnaire?.specialStatusNonPatientFacing === "yes") reasons.push("Non-Patient Facing");
    if (questionnaire?.specialStatusRural === "yes") reasons.push("Rural Area");
    if (questionnaire?.specialStatusHpsa === "yes") reasons.push("HPSA");

    return reasons.length > 0 ? reasons.join(", ") : "";
  }

  get isInBothMode(): boolean {
    return this.multiPacketMode()?.enabled === true;
  }

  getActivityPoints(activity: ImprovementActivity): number {
    // Compute fresh rather than trust activity.points, which only reflects special-status
    // doubling after a save round-trip completes (it's seeded from static per-activity weight
    // data and only overwritten once the API echoes back scored points).
    return activity.weight === "high" || this.hasSpecialStatus ? 40 : 20;
  }

  get estimatedTotalTraditional(): number {
    return this.sumSelectedActivityPoints(this.traditionalActivities, this.traditionalSelectedIds);
  }

  get estimatedTotalMvp(): number {
    return this.sumSelectedActivityPoints(this.mvpActivities, this.mvpSelectedIds);
  }

  get traditionalRequirementMet(): boolean {
    return this.estimatedTotalTraditional >= 40;
  }

  get mvpRequirementMet(): boolean {
    return this.estimatedTotalMvp >= 40;
  }

  // ============================================
  // LIFECYCLE HOOKS
  // ============================================

  ngOnInit(): void {
    this.iaForm = this.fb.group({
      requirementMet: [false, Validators.requiredTrue],
    });

    setTimeout(() => {
      this.parentForm = this.ctrlContainer.form;
      if (this.parentForm) {
        this.parentForm.addControl("ia", this.iaForm);
      }

      this.startDateErrorMatcher = new CustomErrorStateMatcher(
        () => this.performanceStartDateInvalid,
      );
      this.endDateErrorMatcher = new CustomErrorStateMatcher(
        () => this.performanceEndDateInvalid,
      );

      if (this.lastLoadedPerformanceYear === undefined) {
        this.loadActivities();
      }
      this.organizeCategories();
      this.loadDatesFromPacket();
      this.updateParentFormValidity();
    });

    this.iaForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateParentFormValidity();
    });

    this.activityChanged$
      .pipe(debounceTime(2000), takeUntil(this.destroy$))
      .subscribe(() => {
        this.saveIAData();
      });
  }

  ngOnDestroy(): void {
    if (this.parentForm) {
      this.parentForm.removeControl("ia");
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // DATA LOADING
  // ============================================

  private readonly MVP_ONLY_ACTIVITY_IDS_BASE = ['IA_MVP', 'IA_PCMH'];
  private readonly MVP_ONLY_ACTIVITY_IDS_2026_PLUS = ['IA_MVP'];

  private loadActivities(): void {
    const performanceYear = this.currentPacket()?.performanceYear ?? 2025;
    const source = performanceYear >= 2026 ? IA_ACTIVITIES_2026 : IA_ACTIVITIES_2025;
    const mvpOnlyIds =
      performanceYear >= 2026
        ? this.MVP_ONLY_ACTIVITY_IDS_2026_PLUS
        : this.MVP_ONLY_ACTIVITY_IDS_BASE;
    this.allActivities = JSON.parse(JSON.stringify(source));
    this.traditionalActivities = this.allActivities.filter(
      (activity) => !mvpOnlyIds.includes(activity.activityId),
    );
    this.mvpActivities = this.allActivities.filter((activity) => activity.isMvpMeasure);
  }

  private resetActivities(): void {
    this.traditionalSelectedIds.clear();
    this.mvpSelectedIds.clear();
    this.traditionalActivities.forEach((activity) => {
      activity.selected = false;
      activity.documentId = undefined;
      activity.documentName = undefined;
      activity.documentUrl = undefined;
      activity.supportingInfo = undefined;
    });
    this.mvpActivities.forEach((activity) => {
      activity.selected = false;
      activity.documentId = undefined;
      activity.documentName = undefined;
      activity.documentUrl = undefined;
      activity.supportingInfo = undefined;
    });
    this.performanceStartDate = null;
    this.performanceEndDate = null;
  }

  private loadDatesFromPacket(): void {
    this.performanceStartDateInvalid = false;
    this.performanceEndDateInvalid = false;

    const packet = this.currentPacket();
    const iaData = packet?.improvementActivities as any;
    if (iaData?.dateRangeStart && iaData?.dateRangeEnd) {
      this.performanceStartDate = new Date(iaData.dateRangeStart);
      this.performanceEndDate = new Date(iaData.dateRangeEnd);
      this.cdr.markForCheck();
      return;
    }

    if (!this.performanceStartDate) {
      this.performanceStartDate = getMipsPerformanceDates().MIN_DATE;
    }

    if (!this.performanceEndDate) {
      this.performanceEndDate = getMipsPerformanceDates().MAX_DATE;
    }
    this.cdr.markForCheck();
  }

  private loadFromPacket(traditionalIAData?: any, mvpIAData?: any): void {
    const packet = this.currentPacket();
    const mode = this.multiPacketMode();


    const isBothModeWithDualData = mode?.enabled && (traditionalIAData || mvpIAData);

    this.resetActivities();

    if (isBothModeWithDualData) {


      if (traditionalIAData) {

        let traditionalLoadedCount = 0;
        let traditionalSelectedCount = 0;

        this.traditionalActivities.forEach((activity) => {
          const savedActivity = traditionalIAData.activities?.find(
            (a: any) => a.activityId === activity.activityId,
          );

          if (savedActivity) {
            traditionalLoadedCount++;
            activity.selected = savedActivity.selected;
            activity.documentId = savedActivity.documentId;
            activity.documentName = savedActivity.documentName;
            activity.documentUrl = savedActivity.documentUrl;
            activity.supportingInfo = savedActivity.supportingInfo;
            if (savedActivity.points !== undefined) activity.points = savedActivity.points;
            if (savedActivity.isHighWeighted !== undefined) activity.isHighWeighted = savedActivity.isHighWeighted;

            if (savedActivity.selected) {
              traditionalSelectedCount++;
              this.traditionalSelectedIds.add(activity.activityId);
            }
          }
        });
      }

      if (mvpIAData) {

        let mvpLoadedCount = 0;
        let mvpSelectedCount = 0;

        this.mvpActivities.forEach((activity) => {
          const savedActivity = mvpIAData.activities?.find(
            (a: any) => a.activityId === activity.activityId,
          );

          if (savedActivity) {
            mvpLoadedCount++;
            activity.selected = savedActivity.selected;
            activity.documentId = savedActivity.documentId;
            activity.documentName = savedActivity.documentName;
            activity.documentUrl = savedActivity.documentUrl;
            activity.supportingInfo = savedActivity.supportingInfo;
            if (savedActivity.points !== undefined) activity.points = savedActivity.points;
            if (savedActivity.isHighWeighted !== undefined) activity.isHighWeighted = savedActivity.isHighWeighted;

            if (savedActivity.selected) {
              mvpSelectedCount++;
              this.mvpSelectedIds.add(activity.activityId);
            }
          }
        });
      }
    } else {
      const iaData = packet?.improvementActivities;

      if (!iaData) {
        return;
      }

      const reportType = packet.reportingType || "traditional";

      if (reportType === "traditional") {
        let traditionalLoadedCount = 0;
        let traditionalSelectedCount = 0;

        this.traditionalActivities.forEach((activity) => {
          const savedActivity = iaData.activities?.find(
            (a) => a.activityId === activity.activityId,
          );

          if (savedActivity) {
            traditionalLoadedCount++;
            activity.selected = savedActivity.selected;
            activity.documentId = savedActivity.documentId;
            activity.documentName = savedActivity.documentName;
            activity.documentUrl = savedActivity.documentUrl;
            activity.supportingInfo = savedActivity.supportingInfo;
            if (savedActivity.points !== undefined) activity.points = savedActivity.points;
            if (savedActivity.isHighWeighted !== undefined) activity.isHighWeighted = savedActivity.isHighWeighted;

            if (savedActivity.selected) {
              traditionalSelectedCount++;
              this.traditionalSelectedIds.add(activity.activityId);
            }
          }
        });
      }

      if (reportType === "mvp") {
        let mvpLoadedCount = 0;
        let mvpSelectedCount = 0;

        this.mvpActivities.forEach((activity) => {
          const savedActivity = iaData.activities?.find(
            (a) => a.activityId === activity.activityId,
          );

          if (savedActivity) {
            mvpLoadedCount++;
            activity.selected = savedActivity.selected;
            activity.documentId = savedActivity.documentId;
            activity.documentName = savedActivity.documentName;
            activity.documentUrl = savedActivity.documentUrl;
            activity.supportingInfo = savedActivity.supportingInfo;
            if (savedActivity.points !== undefined) activity.points = savedActivity.points;
            if (savedActivity.isHighWeighted !== undefined) activity.isHighWeighted = savedActivity.isHighWeighted;

            if (savedActivity.selected) {
              mvpSelectedCount++;
              this.mvpSelectedIds.add(activity.activityId);
            }
          }
        });
      }
    }

    this.updateParentFormValidity();
  }

  private organizeCategories(): void {
    this.categories = [];

    const performanceYear = this.currentPacket()?.performanceYear ?? 2025;
    const categoryOrder = performanceYear >= 2026 ? IA_CATEGORY_ORDER_2026 : IA_CATEGORY_ORDER_2025;

    categoryOrder.forEach((categoryName: string) => {
      const categoryActivities = this.activeActivities.filter(
        (a) => a.category === categoryName,
      );

      if (categoryActivities.length > 0) {
        this.categories.push({
          name: categoryName,
          activities: categoryActivities,
          isExpanded: true,
        });
      }
    });
  }

  // ============================================
  // USER INTERACTIONS
  // ============================================

  onTabChange(tab: "traditional" | "mvp"): void {
    this.currentTab = tab;
    this.organizeCategories();
  }

  toggleCategory(category: IACategory): void {
    category.isExpanded = !category.isExpanded;
  }

  onActivityToggle(activity: ImprovementActivity): void {
    const targetSet =
      (this.showTabs && this.currentTab === "mvp") ||
      (!this.showTabs && this.reportingType === "mvp")
        ? this.mvpSelectedIds
        : this.traditionalSelectedIds;

    const isSelected = targetSet.has(activity.activityId);

    if (isSelected && activity.documentName) {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: "450px",
        disableClose: true,
        data: {
          title: "Activity Deselected",
          content: `${activity.activityId} has been deselected. Your uploaded document (${activity.documentName}) will not be removed. You can re-select this activity at any time and the document will still be available.`,
          confirmText: "OK",
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        targetSet.delete(activity.activityId);
        activity.selected = false;
        this.updateParentFormValidity();
        this.activityChanged$.next();
        this.cdr.detectChanges();
      });

      return;
    }

    if (isSelected) {
      targetSet.delete(activity.activityId);
      activity.selected = false;
    } else {
      targetSet.add(activity.activityId);
      activity.selected = true;
    }

    this.updateParentFormValidity();
    this.activityChanged$.next();
  }

  isActivitySelected(activity: ImprovementActivity): boolean {
    if (this.reportingType === "both") {
      return this.currentTab === "traditional"
        ? this.traditionalSelectedIds.has(activity.activityId)
        : this.mvpSelectedIds.has(activity.activityId);
    } else if (this.reportingType === "mvp") {
      return this.mvpSelectedIds.has(activity.activityId);
    } else {
      return this.traditionalSelectedIds.has(activity.activityId);
    }
  }

  isActivityIncomplete(activity: ImprovementActivity): boolean {
    return (
      this.isActivitySelected(activity) &&
      !activity.documentName &&
      !activity.documentUrl &&
      !activity.documentId
    );
  }

  get incompleteActivitiesCount(): number {
    return this.activeActivities.filter((a) => this.isActivityIncomplete(a))
      .length;
  }

  get hasIncompleteActivities(): boolean {
    return this.incompleteActivitiesCount > 0;
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return true;
    return date >= this.minDate && date <= this.maxDate;
  };

  onPerformanceStartDateChange(date: Date | null): void {
    if (date && !this.validateDateInRange(date)) {
      this.performanceStartDateInvalid = true;
      this.performanceStartDate = date;
      this.updateParentFormValidity();
      this.cdr.markForCheck();
      return;
    }

    this.performanceStartDateInvalid = false;
    this.performanceStartDate = date;

    this.validateDateRange();

    setTimeout(() => {
      this.updateParentFormValidity();
      this.activityChanged$.next();
    });
  }

  onPerformanceEndDateChange(date: Date | null): void {
    if (date && !this.validateDateInRange(date)) {
      this.performanceEndDateInvalid = true;
      this.performanceEndDate = date;
      this.updateParentFormValidity();
      this.cdr.markForCheck();
      return;
    }

    this.performanceEndDateInvalid = false;
    this.performanceEndDate = date;

    this.validateDateRange();

    setTimeout(() => {
      this.updateParentFormValidity();
      this.activityChanged$.next();
    });
  }

  validateDateInRange(date: Date | null): boolean {
    if (!date) {
      return false;
    }
    const isValid = date >= this.minDate && date <= this.maxDate;
    return isValid;
  }

  validateDateRange(): void {
    this.dateRangeInvalid = false;
    this.startDateBeforeEndDateError = false;

    if (!this.performanceStartDate || !this.performanceEndDate) {
      return;
    }

    if (this.performanceStartDate > this.performanceEndDate) {
      this.startDateBeforeEndDateError = true;
      this.dateRangeInvalid = true;
    }
  }

  // ============================================
  // DOCUMENT OPERATIONS
  // ============================================

  onDocumentUpload(activity: ImprovementActivity, event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!this.isActivitySelected(activity)) {
      return;
    }

    if (!input.files || !input.files[0]) return;

    const file = input.files[0];
    const maxSize = MAX_FILE_SIZE;

    if (file.size > maxSize) {
      this.snackBar.open("File size must be less than 25MB", "Close", {
        duration: 5000,
        panelClass: ["error-snackbar"],
      });
      input.value = "";
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open("Only PDF, JPG, and PNG files are allowed", "Close", {
        duration: 5000,
        panelClass: ["error-snackbar"],
      });
      input.value = "";
      return;
    }

    const packetId = this.getTabPacketId();

    if (!packetId) {
      this.snackBar.open("No submission found", "Close", {
        duration: 5000,
        panelClass: ["error-snackbar"],
      });
      input.value = "";
      return;
    }

    this.store.dispatch(
      MipsPacketActions.uploadIADocument({
        packetId: packetId,
        measureId: activity.activityId,
        file: file,
      }),
    );

    input.value = "";
  }

  private getTabPacketId(): number | undefined {
    const mode = this.multiPacketMode();
    if (mode?.enabled) {
      return this.currentTab === "traditional"
        ? mode.traditionalPacketId
        : mode.mvpPacketId;
    }
    return this.currentPacket()?.id;
  }

  viewFile(activity: ImprovementActivity): void {
    const packetId = this.getTabPacketId();
    if (!packetId || !activity.documentName) return;

    this.store.dispatch(
      MipsPacketActions.viewIADocument({
        packetId,
        measureId: activity.activityId,
        documentName: activity.documentName,
      }),
    );
  }

  downloadFile(activity: ImprovementActivity): void {
    const packetId = this.getTabPacketId();
    if (!packetId || !activity.documentName) return;

    this.store.dispatch(
      MipsPacketActions.downloadIADocument({
        packetId,
        measureId: activity.activityId,
        documentName: activity.documentName,
        asAttachment: true,
      }),
    );
  }

  removeDocument(activity: ImprovementActivity, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const packetId = this.getTabPacketId();
    if (!packetId) return;

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: "450px",
      data: {
        title: "Delete Document",
        content: `Are you sure you want to delete "${activity.documentName}"? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(
          MipsPacketActions.deleteIADocument({
            packetId,
            measureId: activity.activityId,
          }),
        );
      }
    });
  }

  // ============================================
  // FORM VALIDATION & SAVING
  // ============================================

  private updateParentFormValidity(): void {
    if (this.parentForm) {
      const excluded = this.validationService.getExcludedCategories(this.questionnaire());
      if (excluded.has("ia")) {
        this.parentForm.setErrors(null);
        return;
      }

      const datesValid =
        !this.performanceStartDateInvalid &&
        !this.performanceEndDateInvalid &&
        !this.dateRangeInvalid;
      const datesProvided =
        this.performanceStartDate && this.performanceEndDate;

      if (this.canProceedToNextStep && datesValid && datesProvided) {
        this.parentForm.setErrors(null);
        this.iaForm.patchValue({ requirementMet: true }, { emitEvent: false });
      } else {
        this.parentForm.setErrors({ incomplete: true });
        this.iaForm.patchValue({ requirementMet: false }, { emitEvent: false });
      }
    }
  }

  private saveIAData(): void {
    const packet = this.currentPacket();

    if (!packet?.id || !this.performanceStartDate || !this.performanceEndDate) {
      return;
    }

    if (this.performanceStartDateInvalid || this.performanceEndDateInvalid) {
      this.snackBar.open(
        `Cannot save: Performance dates must be within ${packet.performanceYear}`,
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    if (this.dateRangeInvalid || this.startDateBeforeEndDateError) {
      this.snackBar.open(
        "Cannot save: Start date must be before end date",
        "Dismiss",
        {
          duration: 5000,
          horizontalPosition: "center",
          verticalPosition: "top",
          panelClass: ["error-snackbar"],
        },
      );
      return;
    }

    const mode = this.multiPacketMode();
    const performanceStart = this.normalizeDateForSave(
      this.performanceStartDate,
    );
    const performanceEnd = this.normalizeDateForSave(this.performanceEndDate);

    if (mode?.enabled && mode.traditionalPacketId && mode.mvpPacketId) {

      const traditionalActivities = this.traditionalActivities.filter((a) =>
        this.traditionalSelectedIds.has(a.activityId),
      );

      const traditionalMeasurements = traditionalActivities.map((a) => ({
        measureId: a.activityId,
        booleanValue: true,
      }));

      const mvpActivities = this.mvpActivities.filter((a) =>
        this.mvpSelectedIds.has(a.activityId),
      );

      const mvpMeasurements = mvpActivities.map((a) => ({
        measureId: a.activityId,
        booleanValue: true,
      }));

      this.store.dispatch(
        MipsPacketActions.saveIAMeasurements({
          packetId: mode.traditionalPacketId,
          performanceStart: performanceStart!,
          performanceEnd: performanceEnd!,
          measurements: traditionalMeasurements,
        }),
      );

      this.store.dispatch(
        MipsPacketActions.saveIAMeasurements({
          packetId: mode.mvpPacketId,
          performanceStart: performanceStart!,
          performanceEnd: performanceEnd!,
          measurements: mvpMeasurements,
        }),
      );
    } else {

      const reportingType = packet.reportingType || "traditional";

      const selectedIds =
        reportingType === "mvp"
          ? this.mvpSelectedIds
          : this.traditionalSelectedIds;
      const activities =
        reportingType === "mvp"
          ? this.mvpActivities
          : this.traditionalActivities;
      const selectedActivities = activities.filter((a) =>
        selectedIds.has(a.activityId),
      );

      // Dispatch even when empty — the backend correctly clears the measurement set to reflect
      // zero selected activities; skipping the save here would leave stale, non-empty data
      // persisted (and previously showing) after the user removed their last activity.
      const measurements = selectedActivities.map((a) => ({
        measureId: a.activityId,
        booleanValue: true,
      }));

      this.store.dispatch(
        MipsPacketActions.saveIAMeasurements({
          packetId: packet.id,
          performanceStart: performanceStart!,
          performanceEnd: performanceEnd!,
          measurements: measurements,
        }),
      );
    }
  }

  private normalizeDateForSave(date: Date | null): Date | null {
    if (!date) return null;

    const normalized = new Date(date);

    if (isNaN(normalized.getTime()) || normalized.getFullYear() < 1900 || normalized.getFullYear() > 2100) {
      return null;
    }

    normalized.setHours(12, 0, 0, 0);

    return normalized;
  }


  // ============================================
  // UTILITIES
  // ============================================

  isSuspendedActivity(activityId: string): boolean {
    const performanceYear = this.currentPacket()?.performanceYear ?? 2025;
    if (performanceYear >= 2026) {
      return false;
    }
    const suspendedActivities2025 = [
      "IA_AHE_5",
      "IA_AHE_8",
      "IA_AHE_9",
      "IA_AHE_11",
      "IA_AHE_12",
      "IA_PM_6",
      "IA_ERP_3",
      "IA_PM_26",
    ];
    return suspendedActivities2025.includes(activityId);
  }

  getSuspendedActivityTooltip(): string {
    const performanceYear = this.currentPacket()?.performanceYear ?? 2025;
    return `This activity has been suspended for the ${performanceYear} performance period. However, if this activity has already been completed or was in the process of being completed, you can still attest to completing it and receive credit.`;
  }

  private formatDate(date: string | Date): string {
    if (!date) return "";
    const d = new Date(date);
    return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
      .getDate()
      .toString()
      .padStart(2, "0")}/${d.getFullYear()}`;
  }
}
